import { Icons } from '@/components/icons';
import RefundSummary from '@/components/manage/refund/summary/RefundSummary';
import TravelPassAccountValidity from '@/components/manage/travel-passes/TravelPassAccountValidity';
import PriceDisplay from '@/components/PriceDisplay';
import { Typography } from '@/components/primitives/Typography';
import {
  manageFlowBookingSelector,
  manageFlowTravelAccountsMapSelector,
  refundDetailsSelector,
  refundSelectedNonTripAdmissionsSelector,
} from '@/features/manage/manageSelectors';
import { useSeparateNonTripOffersBasedOnType } from '@/hooks/useSeparateNonTripOffersBasedOnType';
import { TransText } from '@/i18n/trans/text';
import { useSelector } from '@/store/utils';
import { type DecoratedTravelPassDetails } from '@/types/travelpass';
import { type FC, useMemo } from 'react';

interface NonTripOffersByRouteAndDates {
  origin?: string;
  destination?: string;
  validFrom: DecoratedTravelPassDetails['validFrom'];
  validUntil: DecoratedTravelPassDetails['validUntil'];
  nonTripOffers: Array<DecoratedTravelPassDetails>;
}

export const RefundNonTripOverview: FC = () => {
  const booking = useSelector(manageFlowBookingSelector);
  const travelAccountsMap = useSelector(manageFlowTravelAccountsMapSelector);
  const { travelPass, multiRide } = useSeparateNonTripOffersBasedOnType(
    booking?.nonTripOffers,
    travelAccountsMap
  );
  const selectedNonTripAdmissions = useSelector(
    refundSelectedNonTripAdmissionsSelector
  );
  const refundSummary = useSelector(refundDetailsSelector);

  const nonTripOffersByRouteAndDates = useMemo(
    () =>
      [...travelPass, ...multiRide].reduce<
        Record<string, NonTripOffersByRouteAndDates>
      >((acc, nonTripOffer) => {
        if (!nonTripOffer.id || !selectedNonTripAdmissions[nonTripOffer.id]) {
          return acc;
        }

        const zone =
          nonTripOffer.travelAccount?.regionalConstraint?.regionalValidities[0]
            .zone;

        const { validFrom, validUntil } = nonTripOffer;
        const origin = zone?.entryStation?.name;
        const destination = zone?.terminalStation?.name;
        const key = `${origin}_${destination}_${validFrom}_${validUntil}`;

        if (acc[key]) {
          acc[key].nonTripOffers.push(nonTripOffer);
        } else {
          acc[key] = {
            origin,
            destination,
            validFrom,
            validUntil,
            nonTripOffers: [nonTripOffer],
          };
        }

        return acc;
      }, {}),
    [multiRide, selectedNonTripAdmissions, travelPass]
  );

  if (!refundSummary || !refundSummary.refundOffers) {
    return null;
  }

  return (
    <div className="p-4">
      <RefundSummary
        refundSummary={refundSummary}
        admissions={Object.values(selectedNonTripAdmissions)}
      />
      <Typography variant="subtitle" className="my-5">
        <TransText i18nKey="youAreAboutToRefundFollowingItems" />
      </Typography>
      <div className="flex flex-col rounded-md border border-neutral-light">
        <div className="flex gap-2 rounded-t-md bg-primary-pale p-4">
          <Icons.transportFrom className="h-5 w-5 text-primary" />
          <Typography variant="body1-bold">
            <TransText i18nKey="travelPassDetails" />
          </Typography>
        </div>
        <div className="flex flex-col gap-4 p-4">
          {Object.entries(nonTripOffersByRouteAndDates).map(([key, item]) => (
            <div key={key}>
              <div className="mb-3 flex flex-row flex-wrap items-center text-primary">
                <Typography variant="body1-bold" className="min-w-fit">
                  {item.origin}
                </Typography>
                <div className="mx-1.5 shrink-0">—</div>
                <Typography variant="body1-bold" className="min-w-fit">
                  {item.destination}
                </Typography>
              </div>
              {item.validFrom && item.validUntil && (
                <TravelPassAccountValidity
                  validFrom={item.validFrom}
                  validUntil={item.validUntil}
                  className="mb-4"
                />
              )}
              <div className="flex flex-col gap-2">
                {item.nonTripOffers.map((nonTripOffer) => (
                  <div key={nonTripOffer.id} className="flex flex-col">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icons.ticket className="text-primary" />
                        <Typography variant="body1-bold">
                          {nonTripOffer.shortDescription}
                        </Typography>
                      </div>
                      <Typography variant="body2-bold" className="text-primary">
                        <PriceDisplay amount={nonTripOffer.price?.amount} />
                      </Typography>
                    </div>
                    <div className="ml-6 flex items-center gap-1 text-neutral">
                      {nonTripOffer.travelAccount?.holder?.firstName && (
                        <Typography variant="body2">
                          {nonTripOffer.travelAccount?.holder?.firstName}
                        </Typography>
                      )}
                      {nonTripOffer.travelAccount?.holder?.lastName && (
                        <Typography variant="body2">
                          {nonTripOffer.travelAccount?.holder?.lastName}
                        </Typography>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RefundNonTripOverview;
